import { MenuItem, Tooltip } from "@components";
import {
  ChartMixedRegular,
  ClockLight,
  CoinsLight,
  CreditCardLight,
  DiagramProjectRegular,
  DotRegular,
  FileInvoiceLight,
  FileLinesLight,
  MegaphoneRegular,
  UserLight,
} from "@icons";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import {
  CLIENT_LIST_PATH,
  DASHBOARD_PATH,
  FORMS_LIST_PATH,
  INVOICE_LIST_PATH,
  PROJECT_LIST_PATH,
  RECURRING_INVOICE_LIST_PATH,
  SURCHARGE_LIST_PATH,
  TAXES_LIST_PATH,
  TIME_ENTRIES_LIST_PATH,
  TRANSACTIONS_LIST_PATH,
} from "../paths";
import Organization from "./organization";

interface MenuItemComponentPropTypes {
  minimized: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

export const memberMainMenuList = [
  {
    key: "organization-menu-item",
    component: (props: MenuItemComponentPropTypes) => {
      return <Organization minimized={props.minimized} />;
    },
  },
  {
    key: "dashboard",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={DASHBOARD_PATH()}
          icon={<ChartMixedRegular width={16} />}
          label={t("member.main.dashboard.dashboard")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "dashboard-divider",
    component: () => (
      <div className="flex w-full my-1 h-0 border-b border-gray-200" />
    ),
  },
  {
    key: "client",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={CLIENT_LIST_PATH()}
          icon={<UserLight width={16} />}
          label={t("clients")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "after-client-divider",
    component: () => (
      <div className="flex w-full my-1 h-0 border-b border-gray-200" />
    ),
  },
  {
    key: "projects",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={PROJECT_LIST_PATH()}
          icon={<DiagramProjectRegular width={16} />}
          label={t("member.main.projects.projectList")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "time-entries",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={TIME_ENTRIES_LIST_PATH()}
          icon={<ClockLight width={16} />}
          label={t("member.main.timeEntries.timeEntries")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "dafter-client-divider",
    component: () => (
      <div className="flex w-full my-1 h-0 border-b border-gray-200" />
    ),
  },
  {
    key: "invoices-menu",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          submenu={[
            {
              key: "invoices",
              component: () => (
                <MenuItem
                  to={INVOICE_LIST_PATH()}
                  icon={<DotRegular width={13} />}
                  label={t("member.main.invoices.invoices")}
                  minimized={props.minimized}
                  setMobileMenuOpen={props.setMobileMenuOpen}
                />
              ),
            },
            {
              key: "recurring-invoices",
              component: () => (
                <MenuItem
                  to={RECURRING_INVOICE_LIST_PATH()}
                  icon={<DotRegular width={13} />}
                  label={t("member.main.invoices.recurring")}
                  minimized={props.minimized}
                  setMobileMenuOpen={props.setMobileMenuOpen}
                />
              ),
            },
          ]}
          icon={<FileInvoiceLight width={13} />}
          label={t("invoices")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "transactions",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={TRANSACTIONS_LIST_PATH()}
          icon={<CreditCardLight width={16} />}
          label={t("transactions")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "fees",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          submenu={[
            {
              key: "taxes",
              component: () => (
                <MenuItem
                  to={TAXES_LIST_PATH()}
                  icon={<DotRegular width={13} />}
                  label={t("taxes")}
                  minimized={props.minimized}
                  setMobileMenuOpen={props.setMobileMenuOpen}
                />
              ),
            },
            {
              key: "surcharges",
              component: () => (
                <MenuItem
                  to={SURCHARGE_LIST_PATH()}
                  icon={<DotRegular width={13} />}
                  label={t("surcharges")}
                  minimized={props.minimized}
                  setMobileMenuOpen={props.setMobileMenuOpen}
                />
              ),
            },
          ]}
          icon={<CoinsLight />}
          label={t("feesAndTaxes")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "dafter-fees-divider",
    component: () => (
      <div className="flex w-full h-0 my-1 border-b border-gray-200" />
    ),
  },
  {
    key: "forms",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={FORMS_LIST_PATH()}
          icon={<FileLinesLight width={16} />}
          label={t("member.main.forms.forms")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
];

export const memberExtraMenuList = [
  {
    key: "change-logs",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <button
          data-canny-changelog
          className={classNames(
            "flex rounded items-center justify-center hover:bg-white hover:text-secondary-700",
            {
              "w-11 h-11 mx-auto": props.minimized,
              "w-full h-8": !props.minimized,
            },
          )}
          aria-label={t("member.main.viewChangeLogs")}
        >
          <Tooltip label={t("member.main.viewChangeLogs")}>
            {props.minimized && <MegaphoneRegular width={20} />}
          </Tooltip>
          {!props.minimized && (
            <span className="ml-2 text-sm">
              {t("member.main.viewChangeLogs")}
            </span>
          )}
        </button>
      );
    },
  },
];
